import { useState } from "react";
import { useTranslation } from "react-i18next";
import produce from "immer";
import { useSnackbar } from "notistack";
import {
  Box,
  Divider,
  Paper,
  SwitchProps,
  Typography,
} from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ChipInput from "material-ui-chip-input";
import SwitchField from "components/Forms/SwitchField";
import WithIconHelper from "components/WithIconHelper";
import ButtonWithLoading from "components/ButtonWithLoading";
import apiRequest from "utils/apiRequestWithErrorCode";
import isEmail from "utils/isEmail";
import { isBlank } from "utils/string";
import EmailAlertsConfiguration from "./EmailAlertsConfiguration";
import { FieldWrapper } from "./Styled";
import { AlertFields, FormState } from "./types";
import { toFormState, toNotificationSettings } from "./utils";

import { NotificationSettings } from "@dashboard-v3/api";
import { WritableDraft } from "immer/dist/internal";

interface Props {
  settings: NotificationSettings;
}

export default function SettingsForm({ settings }: Props) {
  const { t } = useTranslation("notificationSettings");
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState<FormState>(toFormState(settings));
  const [isSaving, setIsSaving] = useState(false);

  const updateForm = (cb: (draft: WritableDraft<FormState>) => void) => {
    setForm(prev => produce(prev, cb));
  };

  const onSubmit = async e => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await apiRequest<NotificationSettings>(
        "PUT",
        "/notifications/settings",
        toNotificationSettings(form)
      );
      enqueueSnackbar(t("form.saveSuccess"), {
        variant: "success",
      });
    } catch (e) {
      enqueueSnackbar(t("errors.updateSettings"), { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleMailAddresses = (mail: string, index?: number) => {
    const isAdd = index === undefined && isEmail(mail);

    updateForm(draft => {
      if (isAdd) {
        draft.mailAddresses.value.push(mail);
        draft.mailAddresses.error = "";
      } else {
        draft.mailAddresses.value = draft.mailAddresses.value.filter(d => {
          return d !== mail;
        });

        draft.mailAddresses.error = !draft.mailAddresses.value.length
          ? t("form.mailAddresses.emptyError")
          : draft.mailAddresses.error;
      }
    });
  };

  const handleSwitch: SwitchProps["onChange"] = e => {
    const { name, checked } = e.target;

    updateForm(draft => {
      if (name === "monthlyUsageReport") {
        draft[name] = checked;
      }
    });
  };

  function handleAlertNotifications(fields: AlertFields) {
    updateForm(draft => {
      draft.alertsByType = fields;
    });
  }

  return (
    <Paper>
      <form onSubmit={onSubmit}>
        <FieldWrapper withPaddingTop style={{ width: "80%" }}>
          <Typography style={{ marginBottom: "15px", fontWeight: 500 }}>
            {t("form.mailAddresses.label")}
          </Typography>
          <WithIconHelper
            position="after"
            text={t("common:chipInputHelper")}
            icon={<InfoOutlinedIcon fontSize="small" color="primary" />}
            buttonStyles={{ marginLeft: "10px" }}
          >
            <ChipInput
              variant="outlined"
              fullWidth
              value={form.mailAddresses.value}
              onAdd={handleMailAddresses}
              onDelete={handleMailAddresses}
              fullWidthInput
              style={{ minWidth: "30rem" }}
              helperText={form.mailAddresses.error}
              error={!isBlank(form.mailAddresses.error)}
            />
          </WithIconHelper>
        </FieldWrapper>
        <Divider />
        <FieldWrapper withPaddingTop>
          <SwitchField
            name="monthlyUsageReport"
            label={t("form.monthlyUsageReport.label")}
            description={t("form.monthlyUsageReport.description")}
            value={form.monthlyUsageReport}
            onChange={handleSwitch}
            labelProps={{ variant: "subtitle2" }}
          />
        </FieldWrapper>
        <Divider />
        <EmailAlertsConfiguration
          settings={settings}
          onChange={handleAlertNotifications}
        />
        <Box mt={3} style={{ padding: "0 0 15px 15px" }}>
          <ButtonWithLoading
            disableElevation
            variant="contained"
            color="primary"
            size="small"
            type="submit"
            loading={isSaving}
            disabled={!isBlank(form.mailAddresses.error)}
            onClick={undefined}
          >
            {t("common:saveSettings")}
          </ButtonWithLoading>
        </Box>
      </form>
    </Paper>
  );
}
